import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { api } from '@/lib/api'
import { Link } from '@/app/router'
import { Button } from '@/components/ui/primitives'
import { cn, formatRelative } from '@/lib/utils'

interface NotificationItem {
  id: string
  channel: string
  subject: string | null
  body: string | null
  link: string | null
  read_at: string | null
  created_at: string
}

export function NotificationBell() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationItem[]>('/notifications'),
    refetchInterval: 60_000,
  })

  const markRead = useMutation({
    mutationFn: (id?: string) =>
      api.post('/notifications/read', undefined, { query: id ? { notification_id: id } : {} }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const items = notifications.data ?? []
  const unread = items.filter((item) => !item.read_at).length

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((value) => !value)}
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        aria-expanded={open}
        className="relative"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -end-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--brand-bright)] px-1 text-[10px] font-bold text-[var(--on-brand-bright)] ring-2 ring-[var(--bg)]">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            aria-label="Notifications"
            className="fade-in absolute end-0 top-full z-50 mt-2 w-[22rem] overflow-hidden rounded-[14px] border border-[var(--border-strong)] bg-[var(--surface)] shadow-[var(--shadow-lg)]"
          >
            <header className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
              <span className="text-[13px] font-semibold">Notifications</span>
              {unread > 0 && (
                <button
                  onClick={() => markRead.mutate(undefined)}
                  className="text-[12px] text-[var(--brand)] hover:underline"
                >
                  Mark all read
                </button>
              )}
            </header>

            <div className="max-h-[22rem] overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-3 py-6 text-center text-[13px] text-[var(--text-muted)]">
                  Nothing to catch up on.
                </p>
              ) : (
                <ul>
                  {items.map((item) => {
                    const content = (
                      <>
                        <p className="text-[13px] font-medium">{item.subject}</p>
                        {item.body && (
                          <p className="mt-0.5 text-[12px] text-[var(--text-muted)]">{item.body}</p>
                        )}
                        <p className="mt-1 text-[11px] text-[var(--text-muted)]">
                          {formatRelative(item.created_at)}
                        </p>
                      </>
                    )
                    return (
                      <li key={item.id} className="border-b border-[var(--border)] last:border-0">
                        {item.link ? (
                          <Link
                            to={item.link}
                            onClick={() => {
                              markRead.mutate(item.id)
                              setOpen(false)
                            }}
                            className={cn(
                              'block px-3 py-2.5 hover:bg-[var(--surface-2)]',
                              !item.read_at && 'bg-[var(--brand-subtle)]/40',
                            )}
                          >
                            {content}
                          </Link>
                        ) : (
                          <div
                            className={cn(
                              'px-3 py-2.5',
                              !item.read_at && 'bg-[var(--brand-subtle)]/40',
                            )}
                          >
                            {content}
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
